import { useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const ADMIN_SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes for admins
const USER_SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours for regular users  
const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout

// Store timeout IDs globally to prevent memory leaks
let sessionTimeoutIds: NodeJS.Timeout[] = [];
let warningShown = false;

export const useSessionTimeout = () => {
  // Use useContext directly to avoid throwing error when context is undefined
  const auth = useContext(AuthContext);

  // Early return if auth context is not ready
  if (!auth) {
    return { resetTimeout: () => { } };
  }

  const { user, isAdmin } = auth;

  const getTimeoutDuration = useCallback(() => {
    return isAdmin ? ADMIN_SESSION_TIMEOUT : USER_SESSION_TIMEOUT;
  }, [isAdmin]);

  const showWarning = useCallback(() => {
    if (isAdmin) {
      toast.error("אזהרת תפוגת הפעלה", {
        description: "ההפעלה הניהולית תפוג בעוד 5 דקות. יש לבצע פעולה כדי להמשיך.",
      });
    }
  }, [isAdmin, toast]);

  const handleTimeout = useCallback(async () => {
    if (user && isAdmin) {
      await supabase.auth.signOut();
      toast.error("ההפעלה פגה", {
        description: "ההפעלה הניהולית פגה מטעמי אבטחה. אנא התחבר מחדש.",
      });
    }
  }, [user, isAdmin, toast]);

  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    sessionTimeoutIds.forEach((id: NodeJS.Timeout) => clearTimeout(id));
    sessionTimeoutIds = [];
    warningShown = false;

    if (user) {
      const timeoutDuration = getTimeoutDuration();
      const warningTimeout = timeoutDuration - WARNING_TIME;

      // Set warning timeout (only for admins)
      if (isAdmin && warningTimeout > 0) {
        const warningId = setTimeout(() => {
          if (!warningShown) {
            showWarning();
            warningShown = true;
          }
        }, warningTimeout);
        sessionTimeoutIds.push(warningId);
      }

      // Set session timeout (only for admins)  
      if (isAdmin) {
        const timeoutId = setTimeout(handleTimeout, timeoutDuration);
        sessionTimeoutIds.push(timeoutId);
      }
    }
  }, [user, isAdmin, getTimeoutDuration, showWarning, handleTimeout]);

  useEffect(() => {
    if (user) {
      resetTimeout();

      // Reset timeout on user activity (only for admins)
      if (isAdmin) {
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

        const resetTimeoutHandler = () => {
          resetTimeout();
        };

        events.forEach(event => {
          document.addEventListener(event, resetTimeoutHandler, true);
        });

        return () => {
          events.forEach(event => {
            document.removeEventListener(event, resetTimeoutHandler, true);
          });

          // Clear timeouts on cleanup
          sessionTimeoutIds.forEach((id: NodeJS.Timeout) => clearTimeout(id));
          sessionTimeoutIds = [];
        };
      }
    }
  }, [user, isAdmin, resetTimeout]);

  return { resetTimeout };
};